import pb from "../../lib/pocketbase";
import Container from "../../components/Container";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { Permissions } from "../../lib/consts";
import { IBlogRecord } from "../../lib/types";
import Loading from "../../components/Loading";
import { format } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import Button from "../../components/form/button";
import NewPost from "../../components/studio/blog/NewPost";
import { config } from "../../config";
import { createSlug } from "../../lib/utils";
import { toast } from "../../components/ui/use-toast";

export default function StudioBlog() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [blogPosts, setBlogPosts] = useState<IBlogRecord[]>();

    async function load() {
        const bp: IBlogRecord[] = await pb.collection("blog").getFullList({
            sort: "-published"
        });
        setBlogPosts(bp);
        setIsLoading(false);
    }

    const groupedPosts: { [year: string]: IBlogRecord[] } = {};
    blogPosts?.forEach((post) => {
        const year = new Date(post.published || post.created).getFullYear().toString();
        if (!groupedPosts[year]) {
            groupedPosts[year] = [];
        }
        groupedPosts[year].push(post);
    });

    useEffect(() => {
        if (!pb.authStore.isValid) {
            navigate("/studio/login");
            return;
        }
        if (pb.authStore?.model?.role < Permissions.blog) {
            navigate(-1);
            return;
        }
        load();
    }, []);

    function statusClasses(status: string) {
        if (status === "Draft") return "bg-orange-500";
        if (status === "Scheduled") return "bg-indigo-500";
        if (status === "Public") return "bg-blue-500";
    }

    function copyPostUrl(post: IBlogRecord) {
        navigator.clipboard?.writeText(`${config.base}/blog/${createSlug(post.title)}/${post.id}`);
        toast({
            title: "🤩 Successfully copied url!",
            description: "You have successfully copied the url of this folder."
        });
    }

    return (
        <>
            <Helmet>
                <title>ReinVB Studio — Blog</title>
            </Helmet>
            <Container className="mb-8 mt-4 md:mt-8 lg:mt-16">
                <div className="flex items-center justify-between gap-2">
                    <h2 className="text-3xl font-bold">Blog</h2>
                    <NewPost />
                </div>
                {isLoading ? (
                    <div className="flex w-full items-center justify-center">
                        <Loading />
                    </div>
                ) : (
                    <>
                        {!blogPosts?.length && <p className="mt-8 pl-4 italic text-slate-500">There are currently no blogposts.</p>}
                        {Object.keys(groupedPosts)
                            .reverse()
                            .map((year) => (
                                <div key={year} className="mt-8">
                                    <h2 className="text-xl font-medium">{year}</h2>
                                    <div className="mt-4 flex flex-col gap-4">
                                        {groupedPosts[year].map((post) => (
                                            <div key={post.id} className="group flex items-center justify-between gap-2 rounded-xl border px-4 py-4">
                                                <div className="flex items-center gap-4">
                                                    <span className="text-sm text-slate-400">{format(new Date(post.published || post.created), "dd MMM")}</span>
                                                    <h2 className="text-md font-medium text-slate-600 transition-colors dark:text-slate-300">{post.title}</h2>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className={`rounded-lg px-2 py-1 text-sm text-white ${statusClasses(post.status)}`}>{post.status}</div>
                                                    <Popover>
                                                        <PopoverTrigger className="">
                                                            <Button variant={"outline"} size={"box"} className="">
                                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
                                                                </svg>
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent align="end" className="absolute right-0 z-30 mt-1 w-48 overflow-hidden rounded-lg border bg-white p-2 dark:bg-slate-900">
                                                            <div className="flex flex-col">
                                                                <Link to={`/studio/blog/${post.id}/edit`} className="flex cursor-pointer items-center gap-3 px-4 py-2 text-right hover:opacity-80">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                                                                    </svg>
                                                                    Edit
                                                                </Link>
                                                                <button onClick={() => copyPostUrl(post)} className="flex cursor-pointer items-center gap-3 px-4 py-2 hover:opacity-80">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                                                                    </svg>
                                                                    Copy url
                                                                </button>
                                                            </div>
                                                        </PopoverContent>
                                                    </Popover>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                    </>
                )}
            </Container>
        </>
    );
}
